import {getNonEmptyChemicalStructureQuery, isEmptyChemicalStructureQuery} from 'chemistry/ChemSearchUtils';
import {ChemicalStructureDrawerValue} from 'chemistry/marvinjs-wrappers/ChemicalStructureDrawer';
import {
  getReactionSearchClauseValidationMessage,
  ReactionSearchClause,
  ReactionSearchClauseDraft,
  validateReactionSearchClauseDrafts,
} from 'chemistry/ReactionChemSearchUtils';
import {ChemicalStructureSearchOperator, Operator} from 'search/SearchConstants';

export interface MoleculeChemSearchSubmit {
  mode: 'molecule';
  chemicalStructureQuery: string;
  chemSearchOperator: ChemicalStructureSearchOperator;
  minSimilarity?: number;
}

export interface ReactionChemSearchSubmit {
  mode: 'reaction';
  clauses: Array<ReactionSearchClause>;
  chemSearchOperator: ChemicalStructureSearchOperator;
}

export type ChemSearchSubmit = MoleculeChemSearchSubmit | ReactionChemSearchSubmit;

export type ChemSearchSubmitResult =
  | {type: 'validation_error'; message: string; focusClauseId?: string}
  | {type: 'success'; submit: ChemSearchSubmit; minSimilarityToSave?: number};

function getMinSimilarityToSave(
  chemSearchOperator: ChemicalStructureSearchOperator,
  minSimilarity: number
): number | undefined {
  return chemSearchOperator === Operator.SIMILARITY ? minSimilarity : undefined;
}

export interface MoleculeChemSearchSubmitInput {
  mol: ChemicalStructureDrawerValue;
  chemSearchOperator: ChemicalStructureSearchOperator;
  minSimilarity: number;
}

export function buildMoleculeChemSearchSubmitPayload(
  input: MoleculeChemSearchSubmitInput
): ChemSearchSubmitResult {
  const {mol, chemSearchOperator, minSimilarity} = input;

  if (isEmptyChemicalStructureQuery(mol)) {
    return {
      type: 'validation_error',
      message: 'Draw a chemical structure to search.',
    };
  }

  const minSimilarityToSave = getMinSimilarityToSave(chemSearchOperator, minSimilarity);
  const submit: MoleculeChemSearchSubmit = {
    mode: 'molecule',
    chemicalStructureQuery: getNonEmptyChemicalStructureQuery(mol),
    chemSearchOperator,
    minSimilarity: minSimilarityToSave,
  };

  return {
    type: 'success',
    submit,
    minSimilarityToSave,
  };
}

export interface ReactionChemSearchSubmitInput {
  clauses: Array<ReactionSearchClauseDraft>;
  activeClauseId: string;
  chemSearchOperator: ChemicalStructureSearchOperator;
}

export function buildReactionChemSearchSubmitPayload(
  input: ReactionChemSearchSubmitInput
): ChemSearchSubmitResult {
  const {clauses, activeClauseId, chemSearchOperator} = input;

  const validationResult = validateReactionSearchClauseDrafts(clauses);
  if (validationResult.type === 'error') {
    const {error} = validationResult;
    return {
      type: 'validation_error',
      message: getReactionSearchClauseValidationMessage(error, clauses.length),
      ...(error.type === 'empty_structure' ? {focusClauseId: error.clauseId} : {}),
    };
  }

  const activeClause = clauses.find((clause) => clause.id === activeClauseId) ?? clauses[0];
  const submit: ReactionChemSearchSubmit = {
    mode: 'reaction',
    clauses: validationResult.clauses,
    chemSearchOperator,
  };

  return {
    type: 'success',
    submit,
    minSimilarityToSave: getMinSimilarityToSave(chemSearchOperator, activeClause.minSimilarity),
  };
}
